import { Component, computed, inject, input, linkedSignal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, map } from "rxjs";
import { ImageLoaderService } from "../../../shared/services/image-loader.service";
import { SharedModule } from "../../../shared/shared.module";
import { AgentViz, ShotVizParams } from "../xwai-viz.types";
import { ShotPanel, panelsForType } from "./shot-panels";

/**
 * Renders a "shot" visualization: a shot gather plotted by the `shot_image`
 * endpoint. The agent's `viz_params` is the request body; this component layers
 * a few interactive controls on top:
 *
 *  - Shot ID (select, top) and iteration (slider) re-fetch immediately.
 *  - Panel tabs (predicted / field / interleaved / …) re-fetch immediately; the
 *    available tabs depend on the data `type` (see {@link panelsForType}).
 *  - Start/end trace and scaling are *deferred*: they edit local drafts and only
 *    re-fetch when the user clicks "Apply", so partial edits don't spam the API.
 */
@Component({
  selector: "app-xwai-shot-viz",
  templateUrl: "./xwai-shot-viz.component.html",
  styleUrls: ["./xwai-shot-viz.component.less"],
  imports: [SharedModule],
})
export class XwaiShotVizComponent {
  private readonly imageLoader = inject(ImageLoaderService);

  readonly viz = input.required<AgentViz>();

  readonly job = computed(() => this.viz().job);

  /** The shot's render params (the agent's `shot_image` request body). */
  private readonly shotParams = computed(
    () => this.viz().viz_params as unknown as ShotVizParams,
  );

  /** Data type from the DTO — drives which panel tabs are offered, shown as a pill. */
  protected readonly dataType = computed(() => this.shotParams().type);

  // --- panel tabs ------------------------------------------------------------
  /** Panels available for the current data type. */
  protected readonly panels = computed(() => panelsForType(this.dataType()));
  /** Selected tab key; re-seeds to the first panel when the type changes. */
  protected readonly selectedPanelKey = linkedSignal(() => this.panels()[0]?.key);
  /** The resolved panel object for the selected key. */
  private readonly selectedPanel = computed<ShotPanel | undefined>(
    () =>
      this.panels().find((p) => p.key === this.selectedPanelKey()) ??
      this.panels()[0],
  );

  // --- immediate controls (re-fetch on change) ------------------------------
  // linkedSignal re-seeds from the payload if the agent emits a new shot, while
  // still letting the user override locally.
  // Shot ID is a select; options will come from an "available shots" resource later.
  protected shotId = linkedSignal(() => this.shotParams().shot_id);
  protected shotIteration = linkedSignal(() => this.shotParams().iteration);

  // --- deferred controls (re-fetch only on "Apply") -------------------------
  // Committed values drive the resource; drafts are what the inputs edit.
  protected startTrace = linkedSignal(() => this.shotParams().start_trace);
  protected endTrace = linkedSignal(() => this.shotParams().end_trace);
  protected scaling = linkedSignal<number | null>(() =>
    seedScaling(this.shotParams().scaling),
  );

  protected startTraceDraft = linkedSignal(() => this.shotParams().start_trace);
  protected endTraceDraft = linkedSignal(() => this.shotParams().end_trace);
  protected scalingDraft = linkedSignal<number | null>(() =>
    seedScaling(this.shotParams().scaling),
  );

  /** True when a draft differs from its committed value — enables "Apply". */
  protected readonly pendingChanges = computed(
    () =>
      this.startTraceDraft() !== this.startTrace() ||
      this.endTraceDraft() !== this.endTrace() ||
      this.scalingDraft() !== this.scaling(),
  );

  /** Commit the deferred drafts, triggering a single re-fetch. */
  protected applyChanges(): void {
    this.startTrace.set(this.startTraceDraft());
    this.endTrace.set(this.endTraceDraft());
    this.scaling.set(this.scalingDraft());
  }

  /**
   * Fetch the shot gather. The agent's `viz_params` is the request body for the
   * `shot_image` endpoint; we spread it and override the controlled fields. A
   * single scaling magnitude becomes the symmetric pair `[scaling, -scaling]`
   * (null → `[null, null]`, i.e. auto). The selected panel contributes its
   * `panel` index and interleave flag.
   */
  protected readonly shotResource = rxResource<string, any>({
    params: () => {
      const v = this.viz();
      if (!v || !v.job) return undefined;
      const base = this.shotParams();
      const panel = this.selectedPanel();
      const s = this.scaling();
      return {
        jobId: v.job.id,
        body: {
          ...base,
          shot_id: this.shotId(),
          iteration: this.shotIteration(),
          start_trace: this.startTrace(),
          end_trace: this.endTrace(),
          scaling: s == null ? [null, null] : [-s, s],
          ...(panel?.body.panel != null ? { panel: panel.body.panel } : {}),
          interleave_attrs: {
            ...base.interleave_attrs,
            ...panel?.body.interleave_attrs,
            field_scaling: s == null ? [null, null] : [-s, s],
            predicted_scaling: s == null ? [null, null] : [-s, s],
            field_clipping: null,
            predicted_clipping: null,
          },
        },
      };
    },
    stream: ({ params }) =>
      this.imageLoader.getTraceImageAndClipValue(params.jobId, params.body)!.pipe(
        map((res) => `data:image/png;base64,${res.imageStr}`),
        catchError((err) => {
          throw Error(
            err?.error?.detail || err?.message || "An unexpected error occurred",
          );
        }),
      ),
  });
}

/** Surface a single editable magnitude from the `[min, max]` scaling pair. */
function seedScaling(scaling: ShotVizParams["scaling"]): number | null {
  if (!scaling) return null;
  const [a, b] = scaling;
  if (a == null && b == null) return null;
  return Math.abs((b ?? a) as number) || null;
}
